import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import {
  type CreateAdminExchangeInstructionRequest,
  type CurrencyDetails,
  type CurrencyExchangeInstructionBatchDetails, type CurrencyExchangeInstructionBatchIdentifier,
  type CurrencyExchangeInstructionDetails,
  type CurrencyExchangeInstructionIdentifier,
  type CurrencyExchangeOrderDetails, type CurrencyExchangeOrdersSearchRequest,
  type CurrencyExchangePairDetails
} from '../../api-schema-types/financial/CurrencyExchangeDomainDtos'
import CONFIG from '../../app-configs'
import { ApiAuthStrategies } from '../shared/ApiAuthStrategies'

interface UnlinkInstructionFromBatch {
  batchId: number
  instructionIds: number[]
}

interface PostExchangeRate {
  batchId: number
  exchangeRate: number
}

export const currencyExchangeApi = createApi({
  reducerPath: 'currencyExchangeApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${CONFIG.PROXIMA_BACK_END_V2_BASE_API_URL}/currency-exchanges`,
    prepareHeaders: (headers: Headers, { getState }) => {
      const authToken = ApiAuthStrategies.getBearerTokenHeader()
      headers.set('authorization', authToken)
      return headers
    }
  }),

  tagTypes: ['CurrencyExchangeInstructions', 'CurrencyExchangeInstructionBatches', 'CurrencyExchangeInstructionBatch'],

  endpoints: (builder) => ({
    getCurrencyExchangeActivePairs: builder.query({
      query: () => ({
        url: '/currency-exchange-pairs/active',
        method: 'GET'
      }),
      transformResponse: (response: CurrencyExchangePairDetails[], meta, arg) => currencyExchangeMapper().activePairsMapper(response, meta, arg)

    }),
    searchExchangeOrders: builder.query({
      query: (search: CurrencyExchangeOrdersSearchRequest) => ({
        url: '/exchange-orders/search',
        method: 'GET',
        params: search
      }),
      transformResponse: (response: CurrencyExchangeOrderDetails[], meta, arg) => currencyExchangeMapper().exchangeOrdersMapper(response, meta, arg)
    }),

    // ===============================================================================================================

    getEnabledCurrencies: builder.query({
      query: () => ({
        url: '/currencies',
        method: 'GET'
      }),
      transformResponse: (response: CurrencyDetails[], meta: any, arg: any) => response
    }),

    createInstruction: builder.mutation({
      query: (arg: CreateAdminExchangeInstructionRequest) => ({
        url: 'instructions/create',
        method: 'POST',
        body: {
          assetHolderId: arg.assetHolderId,
          fromCurrencyCode: arg.fromCurrencyCode,
          toCurrencyCode: arg.toCurrencyCode
        }
      }),
      transformResponse: (response: CurrencyExchangeInstructionIdentifier, meta: any, arg: any) => response,
      invalidatesTags: ['CurrencyExchangeInstructions']
    }),

    getPendingExchangeInstructionsInstructions: builder.query({
      query: () => ({
        url: 'instructions/pending',
        method: 'GET',
        params: { }
      }),
      providesTags: ['CurrencyExchangeInstructions'],
      transformResponse: (response: CurrencyExchangeInstructionDetails[], meta: any, arg: any) => response
    }),

    getOpenBatches: builder.query({
      query: (showAllBatches: boolean) => ({
        url: `instructions/batches/${showAllBatches ? 'all' : 'open'}`,
        method: 'GET',
        params: { }
      }),
      providesTags: ['CurrencyExchangeInstructionBatches'],
      transformResponse: (response: CurrencyExchangeInstructionBatchDetails[], meta: any, arg: any) => response
    }),

    getBatch: builder.query({
      query: ({ batchId }) => ({
        url: `instructions/batches/${batchId}/edit`,
        params: { }
      }),
      providesTags: ['CurrencyExchangeInstructionBatch'],
      transformResponse: (response: CurrencyExchangeInstructionBatchDetails, meta: any, arg: any) => response
    }),

    createBatch: builder.mutation({
      query: ({ fromCurrencyCode, toCurrencyCode }) => ({
        url: 'instructions/batches/create',
        method: 'POST',
        body: {
          fromCurrencyCode,
          toCurrencyCode
        }
      }),
      transformResponse: (response: CurrencyExchangeInstructionBatchIdentifier, meta: any, arg: any) => response,
      invalidatesTags: ['CurrencyExchangeInstructions', 'CurrencyExchangeInstructionBatches']
    }),

    processBatch: builder.mutation({
      query: (arg: PostExchangeRate) => ({
        url: `instructions/batches/${arg.batchId}/process`,
        method: 'POST',
        body: {
          exchangeRate: arg.exchangeRate
        }
      }),
      transformResponse: (response: CurrencyExchangeInstructionBatchIdentifier, meta: any, arg: any) => response,
      invalidatesTags: ['CurrencyExchangeInstructionBatch', 'CurrencyExchangeInstructions', 'CurrencyExchangeInstructionBatches']
    }),

    cancelBatch: builder.mutation({
      query: (batchId: number) => ({
        url: `instructions/batches/${batchId}/cancel`,
        method: 'POST'
      }),
      transformResponse: (response: CurrencyExchangeInstructionBatchIdentifier, meta: any, arg: any) => response,
      invalidatesTags: ['CurrencyExchangeInstructionBatch', 'CurrencyExchangeInstructions', 'CurrencyExchangeInstructionBatches']
    }),

    unlinkExchangeInstructions: builder.mutation({
      query: (arg: UnlinkInstructionFromBatch) => ({
        url: `instructions/batches/${arg.batchId}/unlink-instructions`,
        method: 'POST',
        body: {
          instructionIds: arg.instructionIds
        }
      }),
      transformResponse: (response: CurrencyExchangeInstructionBatchIdentifier, meta: any, arg: any) => response,
      invalidatesTags: ['CurrencyExchangeInstructionBatch', 'CurrencyExchangeInstructions', 'CurrencyExchangeInstructionBatches']
    })

  })
})

export const useCurrencyExchangeApiGetActivePairs = currencyExchangeApi.endpoints.getCurrencyExchangeActivePairs.useQuery
export const useCurrencyExchangeApiSearchExchangeOrders = currencyExchangeApi.endpoints.searchExchangeOrders.useQuery

export const useGetPendingExchangeInstructionsInstructionsQuery = currencyExchangeApi.endpoints.getPendingExchangeInstructionsInstructions.useQuery
export const useGetOpenBatchesQuery = currencyExchangeApi.endpoints.getOpenBatches.useQuery
export const useGetBatchQuery = currencyExchangeApi.endpoints.getBatch.useQuery

export const useGetEnabledCurrenciesQuery = currencyExchangeApi.endpoints.getEnabledCurrencies.useQuery
export const useCreateInstructionMutation = currencyExchangeApi.endpoints.createInstruction.useMutation
export const useCreateBatchMutation = currencyExchangeApi.endpoints.createBatch.useMutation
export const useCancelBatchMutation = currencyExchangeApi.endpoints.cancelBatch.useMutation
export const useProcessBatchMutation = currencyExchangeApi.endpoints.processBatch.useMutation
export const useUnlinkExchangeInstructionsMutation = currencyExchangeApi.endpoints.unlinkExchangeInstructions.useMutation

// ====================================================================================================================

 
export const currencyExchangeMapper = () => {
  const activePairsMapper = (responseData: CurrencyExchangePairDetails[], responseMeta: any, responseArgs: any): CurrencyExchangePairDetails[] => {
    console.log(`[API] activePairsMapper Loaded ${responseData.length} pairs`)
    return responseData
  }

  const exchangeOrdersMapper = (responseData: CurrencyExchangeOrderDetails[], responseMeta: any, responseArgs: any): CurrencyExchangeOrderDetails[] => {
    console.log(`[API] exchangeOrdersMapper Loaded ${responseData.length} pairs`)
    return responseData
  }

  return {
    activePairsMapper,
    exchangeOrdersMapper
  }
}
